import {
  type Connection,
  type ConnectionInput,
  connectionSchema,
  type CreatedConnection,
  createdConnectionSchema,
  type MyConnections,
  myConnectionsSchema,
  type NewConnection,
} from "@crux/contracts";
import { z } from "zod";
import { problem } from "./errors.js";

/**
 * Your own AI app connections, across your workspaces (DESIGN.md "AI apps",
 * 2026-10-14). Like the API keys calls, these routes exist only in the Rust
 * API, so they are plain requests parsed with the shared schemas.
 */

const refusalSchema = z.object({ message: z.string() });

async function send(
  path: string,
  init: { readonly method: string; readonly body?: unknown },
): Promise<Response> {
  return await fetch(`/api/account/connections${path}`, {
    method: init.method,
    credentials: "same-origin",
    headers: init.body === undefined ? {} : { "content-type": "application/json" },
    ...(init.body === undefined ? {} : { body: JSON.stringify(init.body) }),
  });
}

/** A refusal in the server's own words: "You cannot change questions in…". */
async function refused(response: Response, attempt: string): Promise<Error> {
  if ([400, 403, 404, 409].includes(response.status)) {
    const parsed = refusalSchema.safeParse(await response.json().catch(() => null));
    if (parsed.success) return new Error(parsed.data.message);
  }
  return new Error(problem(response.status, attempt));
}

export async function fetchMyConnections(): Promise<MyConnections> {
  const response = await send("", { method: "GET" });
  if (!response.ok) throw await refused(response, "load your connections");
  return myConnectionsSchema.parse(await response.json());
}

export async function createConnection(input: NewConnection): Promise<CreatedConnection> {
  const response = await send("", { method: "POST", body: input });
  if (response.status === 429) {
    throw new Error("Too many keys made at once. Wait fifteen minutes and try again.");
  }
  if (!response.ok) throw await refused(response, "create the key");
  return createdConnectionSchema.parse(await response.json());
}

export async function updateConnection(
  id: string,
  input: ConnectionInput,
): Promise<Connection> {
  const response = await send(`/${encodeURIComponent(id)}`, {
    method: "PATCH",
    body: input,
  });
  if (!response.ok) throw await refused(response, "save the connection");
  return connectionSchema.parse(await response.json());
}

export async function rollConnection(id: string): Promise<CreatedConnection> {
  const response = await send(`/${encodeURIComponent(id)}/roll`, { method: "POST" });
  if (!response.ok) throw await refused(response, "roll the key");
  return createdConnectionSchema.parse(await response.json());
}

export async function deleteConnection(id: string): Promise<void> {
  const response = await send(`/${encodeURIComponent(id)}`, { method: "DELETE" });
  if (!response.ok) throw await refused(response, "delete the connection");
}
